import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarX,
  ChevronRight,
} from 'lucide-react';
import {
  getDashboardKPIs,
  getCheckinStatus,
  getPlayers7Days,
  getUpcomingBlocks,
  getUnvalidatedBookings,
} from '../api';
import styles from './DashboardPage.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function formatLongDate(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function greetingWord() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Heatmap bubble colors for the 7-day mini calendar.
 * Returns { bg, text } CSS color strings.
 */
function bubbleColors(count) {
  if (count >= 120) return { bg: '#085041', text: '#E1F5EE' };
  if (count >= 100) return { bg: '#0F6E56', text: '#E1F5EE' };
  if (count >= 80)  return { bg: '#1D9E75', text: '#E1F5EE' };
  if (count >= 60)  return { bg: '#5DCAA5', text: '#085041' };
  if (count >= 40)  return { bg: '#9FE1CB', text: '#085041' };
  return { bg: '#E1F5EE', text: '#085041' };
}

function badgeForBlock(dateLabel, startTimeISO) {
  // Compute urgency class from start_time; use date_label as display text
  const label = dateLabel || '—';
  if (!startTimeISO) return { label, cls: styles.badgeNormal };
  const msAway = new Date(startTimeISO) - new Date();
  const daysAway = Math.floor(msAway / (1000 * 60 * 60 * 24));
  if (daysAway <= 0)  return { label, cls: styles.badgeToday  };
  if (daysAway <= 3)  return { label, cls: styles.badgeUrgent };
  return { label, cls: styles.badgeNormal };
}

function formatBlockTime(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return isoStr;
  }
}

function formatBlockDate(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
    });
  } catch {
    return isoStr;
  }
}

function formatTeeTimeStr(isoOrStr) {
  if (!isoOrStr) return '—';
  try {
    const d = new Date(isoOrStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return isoOrStr;
  }
}

function pct(val, total) {
  if (!total) return 0;
  return Math.round((val / total) * 100);
}

// ---------------------------------------------------------------------------
// Sub-widgets
// ---------------------------------------------------------------------------

function KPIRow({ data, loading, error }) {
  const tiles = [
    { label: 'Players today',       value: data?.players_today,        sub: null              },
    { label: 'Open tee time slots', value: data?.open_tee_time_slots,  sub: 'Available today' },
    { label: 'Carts needed',        value: data?.carts_needed,         sub: null              },
    { label: 'Holes to play today',  value: data?.holes_today,          sub: null              },
  ];

  return (
    <div className={`${styles.row} ${styles.kpiRow}`}>
      {tiles.map((t) => (
        <div key={t.label} className={styles.kpiTile}>
          <div className={styles.kpiLabel}>{t.label}</div>
          {loading && <div className={styles.loadingText}>Loading...</div>}
          {error && !loading && <div className={styles.errorText}>Failed to load</div>}
          {!loading && !error && (
            <>
              <div className={styles.kpiValue}>
                {t.value ?? '—'}
              </div>
              {t.sub && <div className={styles.kpiSub}>{t.sub}</div>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function CheckinStatusCard({ data, loading, error }) {
  const booked  = data?.booked  ?? 0;
  const paid    = data?.paid    ?? 0;
  const noShow  = data?.no_show ?? 0;
  const total   = booked + paid + noShow;

  const pPaid   = pct(paid,   total);
  const pBooked = pct(booked, total);
  const pNoShow = pct(noShow, total);

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>Check-in status</div>
      {loading && <div className={styles.loadingText}>Loading...</div>}
      {error && !loading && <div className={styles.errorText}>Failed to load</div>}
      {!loading && !error && (
        <>
          <div className={styles.checkinBoxes}>
            <div className={`${styles.checkinBox} ${styles.boxPaid}`}>
              <div className={styles.checkinBoxLabel}>Paid</div>
              <div className={styles.checkinBoxValue}>{paid}</div>
            </div>
            <div className={`${styles.checkinBox} ${styles.boxBooked}`}>
              <div className={styles.checkinBoxLabel}>Booked</div>
              <div className={styles.checkinBoxValue}>{booked}</div>
            </div>
            <div className={`${styles.checkinBox} ${styles.boxNoShow}`}>
              <div className={styles.checkinBoxLabel}>No show</div>
              <div className={styles.checkinBoxValue}>{noShow}</div>
            </div>
          </div>
          <div className={styles.progressBar} role="presentation">
            <div
              className={`${styles.progressSegment} ${styles.segPaid}`}
              style={{ width: `${pPaid}%` }}
            />
            <div
              className={`${styles.progressSegment} ${styles.segBooked}`}
              style={{ width: `${pBooked}%` }}
            />
            <div
              className={`${styles.progressSegment} ${styles.segNoShow}`}
              style={{ width: `${pNoShow}%` }}
            />
          </div>
          <div className={styles.checkinLegend}>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.dotPaid}`} />
              Paid {pPaid}%
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.dotBooked}`} />
              Booked {pBooked}%
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.dotNoShow}`} />
              No show {pNoShow}%
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Players7DaysCard({ data, loading, error, todayStr }) {
  const days = data?.days ?? [];

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>Players — next 7 days</div>
      {loading && <div className={styles.loadingText}>Loading...</div>}
      {error && !loading && <div className={styles.errorText}>Failed to load</div>}
      {!loading && !error && (
        <div className={styles.calGrid}>
          {days.map((d) => {
            const [y, m, day] = d.date.split('-').map(Number);
            const dt = new Date(y, m - 1, day);
            const dow = DAY_ABBR[dt.getDay()];
            const isToday = d.date === todayStr;
            const playerCount = d.players ?? d.player_count ?? 0;
            const { bg, text } = bubbleColors(playerCount);

            return (
              <div
                key={d.date}
                className={`${styles.calDay} ${isToday ? styles.calDayToday : ''}`}
              >
                <span className={styles.calDow}>{dow}</span>
                <span className={styles.calDate}>{day}</span>
                <span
                  className={styles.calBubble}
                  style={{ background: bg, color: text }}
                  title={`${playerCount} players`}
                >
                  {playerCount}
                </span>
                {(d.member_count != null || d.guest_count != null) && (
                  <span className={styles.calSub}>
                    {d.member_count ?? 0}m {d.guest_count ?? 0}g
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UpcomingBlocksCard({ data, loading, error }) {
  const blocks = data?.blocks ?? [];

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>Upcoming blocks</div>
      {loading && <div className={styles.loadingText}>Loading...</div>}
      {error && !loading && <div className={styles.errorText}>Failed to load</div>}
      {!loading && !error && (
        <>
          {blocks.length === 0 && (
            <p className={styles.emptyState}>No upcoming blocks in the next 14 days</p>
          )}
          {blocks.length > 0 && (
            <div className={styles.blockList}>
              {blocks.map((b, i) => {
                const { label, cls } = badgeForBlock(b.date_label, b.start_time);
                const blockType = b.block_type ?? b.block_type_name ?? '';
                const courseName = b.golf_course ?? b.golf_course_name ?? '—';
                const dateStr = formatBlockDate(b.start_time);
                const timeRange = b.start_time
                  ? `${formatBlockTime(b.start_time)}${b.end_time ? ` – ${formatBlockTime(b.end_time)}` : ''}`
                  : '';

                return (
                  <div key={i} className={styles.blockRow}>
                    <div className={styles.blockIcon} aria-hidden="true">
                      <CalendarX size={16} strokeWidth={1.5} />
                    </div>
                    <div className={styles.blockMeta}>
                      <div className={styles.blockName}>{b.name}</div>
                      <div className={styles.blockCourse}>
                        {blockType && <>{blockType} &middot; </>}
                        {courseName}
                        {dateStr && <> &middot; {dateStr}</>}
                        {timeRange && <> &middot; {timeRange}</>}
                      </div>
                    </div>
                    <span className={`${styles.blockBadge} ${cls}`}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function UnvalidatedBookingsCard({ data, loading, error }) {
  const navigate = useNavigate();
  const bookings = data?.bookings ?? [];

  // Never render if there's nothing to show (even loading/error: we don't
  // know yet, so we render — the caller controls visibility once data lands)
  return (
    <div className={styles.cardAmber}>
      <div className={styles.amberTitle}>
        <AlertTriangle size={14} strokeWidth={1.5} />
        Unvalidated bookings — CMS was offline
      </div>
      {loading && <div className={styles.loadingText}>Loading...</div>}
      {error && !loading && <div className={styles.errorText}>Failed to load</div>}
      {!loading && !error && (
        <>
          <div className={styles.unvalidatedList}>
            {bookings.map((bk, i) => (
              <div
                key={i}
                className={styles.unvalidatedRow}
                onClick={() => navigate(`/bookings/${bk.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/bookings/${bk.id}`)}
              >
                <AlertTriangle
                  size={14}
                  strokeWidth={1.5}
                  className={styles.warnIcon}
                  aria-hidden="true"
                />
                <div className={styles.unvalidatedMeta}>
                  <div className={styles.unvalidatedMain}>
                    {bk.booking_number} — {bk.booked_by_name}
                  </div>
                  <div className={styles.unvalidatedSub}>
                    {bk.golf_course ?? bk.golf_course_name} &middot; {formatTeeTimeStr(bk.tee_time ?? bk.tee_time_str)} &middot; Member info could not be validated
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            className={styles.viewAllLink}
            onClick={() => navigate('/bookings?is_validated=false')}
          >
            View all
            <ChevronRight size={12} strokeWidth={2} />
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DashboardPage
// ---------------------------------------------------------------------------

export default function DashboardPage({ user }) {
  const today = todayISO();

  const [kpis,    setKpis]    = useState({ data: null, loading: true, error: false });
  const [checkin, setCheckin] = useState({ data: null, loading: true, error: false });
  const [players, setPlayers] = useState({ data: null, loading: true, error: false });
  const [blocks,  setBlocks]  = useState({ data: null, loading: true, error: false });
  const [unval,   setUnval]   = useState({ data: null, loading: true, error: false });

  useEffect(() => {
    const fetches = [
      getDashboardKPIs(today)
        .then((d) => setKpis({ data: d, loading: false, error: false }))
        .catch(() => setKpis({ data: null, loading: false, error: true })),

      getCheckinStatus(today)
        .then((d) => setCheckin({ data: d, loading: false, error: false }))
        .catch(() => setCheckin({ data: null, loading: false, error: true })),

      getPlayers7Days(today)
        .then((d) => setPlayers({ data: d, loading: false, error: false }))
        .catch(() => setPlayers({ data: null, loading: false, error: true })),

      getUpcomingBlocks()
        .then((d) => setBlocks({ data: d, loading: false, error: false }))
        .catch(() => setBlocks({ data: null, loading: false, error: true })),

      getUnvalidatedBookings()
        .then((d) => setUnval({ data: d, loading: false, error: false }))
        .catch(() => setUnval({ data: null, loading: false, error: true })),
    ];

    Promise.allSettled(fetches);
  }, [today]);

  const username = user?.username || 'there';
  const unvalidatedCount = unval.data?.bookings?.length ?? 0;
  const showUnvalidated  = unval.loading || unval.error || unvalidatedCount > 0;

  return (
    <div className={styles.page}>
      {/* Page header */}
      <header className={styles.header}>
        <h1 className={styles.greeting}>
          {greetingWord()}, {username}
        </h1>
        <div className={styles.headerMeta}>
          <span className={styles.dateStr}>{formatLongDate(today)}</span>
          {(unvalidatedCount > 0) && (
            <span className={styles.alertBadge} role="status">
              {unvalidatedCount} unvalidated
            </span>
          )}
        </div>
      </header>

      {/* Row 1: KPI tiles */}
      <KPIRow
        data={kpis.data}
        loading={kpis.loading}
        error={kpis.error}
      />

      {/* Row 2: Check-in status (1) + Players 7 days (2) */}
      <div className={`${styles.row} ${styles.splitRow}`}>
        <CheckinStatusCard
          data={checkin.data}
          loading={checkin.loading}
          error={checkin.error}
        />
        <Players7DaysCard
          data={players.data}
          loading={players.loading}
          error={players.error}
          todayStr={today}
        />
      </div>

      {/* Row 3: Upcoming blocks + Unvalidated bookings (conditional) */}
      <div
        className={`${styles.row} ${showUnvalidated ? styles.twoColRow : ''}`}
        style={showUnvalidated ? {} : { gridTemplateColumns: '1fr' }}
      >
        <UpcomingBlocksCard
          data={blocks.data}
          loading={blocks.loading}
          error={blocks.error}
        />
        {showUnvalidated && (
          <UnvalidatedBookingsCard
            data={unval.data}
            loading={unval.loading}
            error={unval.error}
          />
        )}
      </div>
    </div>
  );
}
